import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { logDev } from "@/lib/log";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ImagePlus, Send as SendIcon, Trash2, MessageSquare, ShieldCheck, Star, X, Smile, Pin, PinOff } from "lucide-react";

const EMOJIS = ["👍", "🔥", "❤️", "😂"];

function timeAgo(iso) {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

function initial(name) { return (name || "?").trim().charAt(0).toUpperCase(); }

function Avatar({ user }) {
  const cls = user.is_admin
    ? "bg-slate-900 text-white"
    : user.is_master
    ? "bg-gradient-to-br from-amber-400 to-amber-600 text-white"
    : "bg-slate-200 text-slate-700";
  return (
    <div className={`w-9 h-9 rounded-full grid place-items-center text-sm font-bold shrink-0 ${cls}`}>
      {user.is_admin ? <ShieldCheck size={16} /> : user.is_master ? <Star size={14} className="fill-white" /> : initial(user.user_name)}
    </div>
  );
}

function PostImage({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(res.data);
        setUrl(blobUrl);
      })
      .catch((err) => logDev(err));
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [path]);
  if (!url) return (
    <div className="mt-3 rounded-lg h-40 bg-slate-100 grid place-items-center text-slate-400 text-xs">Loading image…</div>
  );
  return (
    <img
      src={url}
      alt=""
      className="mt-3 rounded-lg max-h-[420px] w-full max-w-full object-cover border border-slate-200"
      data-testid="wb-post-image"
    />
  );
}

function ReactionBar({ reactions = {}, currentUserId, onToggle, testidPrefix }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const entries = Object.entries(reactions).filter(([, users]) => (users || []).length > 0);
  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap">
      {entries.map(([emoji, users]) => {
        const mine = (users || []).includes(currentUserId);
        return (
          <button
            key={emoji}
            data-testid={`${testidPrefix}-chip-${emoji}`}
            onClick={() => onToggle(emoji)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono-plex border ${mine ? "bg-[#00b4ff]/10 border-[#00b4ff]/50 text-[#00b4ff]" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}
            style={{ transition: "background-color 0.15s ease, border-color 0.15s ease" }}
          >
            <span>{emoji}</span><span className="font-semibold">{users.length}</span>
          </button>
        );
      })}
      <div className="relative">
        <button
          data-testid={`${testidPrefix}-add`}
          onClick={() => setPickerOpen((o) => !o)}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-slate-500 hover:text-[#00b4ff] hover:bg-slate-50"
          style={{ transition: "color 0.15s ease, background-color 0.15s ease" }}
          aria-label="React"
        >
          <Smile size={12} /> React
        </button>
        {pickerOpen && (
          <div className="absolute z-10 -top-11 left-0 bg-white border border-slate-200 rounded-full shadow-lg px-2 py-1 flex gap-1"
               data-testid={`${testidPrefix}-picker`}>
            {EMOJIS.map((e) => (
              <button
                key={e}
                data-testid={`${testidPrefix}-pick-${e}`}
                onClick={() => { onToggle(e); setPickerOpen(false); }}
                className="text-xl w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100 hover:scale-125"
                style={{ transition: "transform 0.15s ease, background-color 0.15s ease" }}
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CommentComposer({ postId, onPosted }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/whiteboard/${postId}/comments`, { text: t });
      setText("");
      onPosted(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex items-center gap-2 mt-3" data-testid={`wb-comment-form-${postId}`}>
      <input
        data-testid={`wb-comment-input-${postId}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Tulis komen…"
        className="flex-1 px-3 py-2 rounded-full bg-slate-100 focus:bg-white border border-transparent focus:border-[#00b4ff] focus:outline-none text-sm"
        style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}
      />
      <button
        data-testid={`wb-comment-send-${postId}`}
        type="submit"
        disabled={!text.trim() || busy}
        className="grid place-items-center w-9 h-9 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-50 text-white"
        style={{ transition: "background-color 0.2s ease" }}
        aria-label="Post comment"
      >
        <SendIcon size={14} className="translate-x-[1px] -translate-y-[1px]" />
      </button>
    </form>
  );
}

function CommentRow({ comment, postId, canDelete, currentUserId, onDelete, onReact }) {
  return (
    <div className="flex items-start gap-2 group" data-testid={`wb-comment-${comment.id}`}>
      <Avatar user={comment} />
      <div className="min-w-0 flex-1">
        <div className="bg-slate-100 rounded-2xl px-3 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-semibold text-xs">{comment.user_name || "member"}</div>
            {comment.is_admin && <span className="px-1.5 py-0.5 rounded bg-slate-900 text-white text-[8px] font-mono-plex">ADMIN</span>}
            {comment.is_master && !comment.is_admin && <span className="px-1.5 py-0.5 rounded bg-amber-500 text-white text-[8px] font-mono-plex">MASTER</span>}
          </div>
          <div className="mt-0.5 text-sm text-slate-800 whitespace-pre-wrap break-words" style={{ overflowWrap: "anywhere" }}>{comment.text}</div>
        </div>
        <ReactionBar
          reactions={comment.reactions}
          currentUserId={currentUserId}
          onToggle={(e) => onReact(postId, comment.id, e)}
          testidPrefix={`wb-comment-react-${comment.id}`}
        />
        <div className="mt-1 flex items-center gap-2 text-[10px] font-mono-plex text-slate-400 px-2">
          <span>{timeAgo(comment.created_at)}</span>
          {canDelete && (
            <button
              data-testid={`wb-comment-delete-${comment.id}`}
              onClick={() => onDelete(comment.id)}
              className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600"
              style={{ transition: "color 0.15s ease, opacity 0.15s ease" }}
              aria-label="Delete comment"
            >
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PostCard({ post, currentUserId, isAdmin, onDelete, onCommentAdded, onCommentDeleted, onReactPost, onReactComment, onTogglePin }) {
  const canDeletePost = isAdmin || post.user_id === currentUserId;
  return (
    <article
      className={`rounded-2xl border p-5 ${post.pinned ? "bg-gradient-to-br from-amber-50/60 via-white to-white border-amber-300 ring-1 ring-amber-200" : "border-slate-200 bg-white"}`}
      data-testid={`wb-post-${post.id}`}
    >
      {post.pinned && (
        <div className="mb-3 flex items-center gap-1.5 text-[10px] font-mono-plex tracking-[0.28em] uppercase text-amber-700">
          <Pin size={11} className="fill-amber-500 text-amber-700" /> Pinned by admin
        </div>
      )}
      <header className="flex items-center gap-3">
        <Avatar user={post} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-semibold text-sm tracking-tight truncate">{post.user_name || "member"}</div>
            {post.is_admin && <span className="px-1.5 py-0.5 rounded bg-slate-900 text-white text-[8px] font-mono-plex tracking-widest">ADMIN</span>}
            {post.is_master && !post.is_admin && <span className="px-1.5 py-0.5 rounded bg-amber-500 text-white text-[8px] font-mono-plex tracking-widest">MASTER</span>}
          </div>
          <div className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-400">{timeAgo(post.created_at)} ago</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isAdmin && (
            <button
              data-testid={`wb-post-pin-${post.id}`}
              onClick={() => onTogglePin(post)}
              className={`p-1.5 rounded ${post.pinned ? "text-amber-600 hover:bg-amber-50" : "text-slate-300 hover:text-amber-600 hover:bg-slate-50"}`}
              style={{ transition: "color 0.15s ease, background-color 0.15s ease" }}
              aria-label={post.pinned ? "Unpin" : "Pin"}
              title={post.pinned ? "Unpin" : "Pin to top"}
            >
              {post.pinned ? <PinOff size={14} /> : <Pin size={14} />}
            </button>
          )}
          {canDeletePost && (
            <button
              data-testid={`wb-post-delete-${post.id}`}
              onClick={() => onDelete(post.id)}
              className="text-slate-300 hover:text-rose-600 p-1.5"
              style={{ transition: "color 0.15s ease" }}
              aria-label="Delete post"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </header>
      {post.text && (
        <div className="mt-3 text-sm leading-relaxed whitespace-pre-wrap break-words" style={{ overflowWrap: "anywhere" }}>{post.text}</div>
      )}
      {post.image_path && <PostImage path={post.image_path} />}

      <ReactionBar
        reactions={post.reactions}
        currentUserId={currentUserId}
        onToggle={(e) => onReactPost(post.id, e)}
        testidPrefix={`wb-post-react-${post.id}`}
      />

      <div className="mt-4 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-1.5 text-[10px] font-mono-plex tracking-widest uppercase text-slate-500 mb-3">
          <MessageSquare size={11} /> {post.comments.length} {post.comments.length === 1 ? "comment" : "comments"}
        </div>
        {post.comments.length > 0 && (
          <div className="space-y-3">
            {post.comments.map((c) => (
              <CommentRow
                key={c.id}
                comment={c}
                postId={post.id}
                canDelete={isAdmin || c.user_id === currentUserId}
                currentUserId={currentUserId}
                onDelete={(cid) => onCommentDeleted(post.id, cid)}
                onReact={onReactComment}
              />
            ))}
          </div>
        )}
        <CommentComposer postId={post.id} onPosted={(c) => onCommentAdded(post.id, c)} />
      </div>
    </article>
  );
}

function Composer({ onPosted }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pickFile = (f) => {
    if (preview) URL.revokeObjectURL(preview);
    if (!f) { setFile(null); setPreview(null); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Image too large (10MB max)"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const clear = () => { pickFile(null); setText(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) { toast.warning("Tulis text atau upload image dulu."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", text.trim());
      if (file) fd.append("image", file);
      const { data } = await api.post("/whiteboard", fd, { headers: { "Content-Type": "multipart/form-data" } });
      clear();
      onPosted(data);
      toast.success("Posted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-4 mb-6" data-testid="wb-composer">
      <textarea
        data-testid="wb-composer-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Share idea, screenshot chart, atau tanya soalan…"
        rows={3}
        maxLength={2000}
        className="w-full resize-none px-3 py-2 rounded-lg bg-slate-50 focus:bg-white border border-transparent focus:border-[#00b4ff] focus:outline-none text-sm leading-relaxed"
        style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}
      />
      {preview && (
        <div className="relative mt-3 inline-block">
          <img src={preview} alt="" className="max-h-60 rounded-lg border border-slate-200" />
          <button
            type="button"
            data-testid="wb-composer-remove-image"
            onClick={() => pickFile(null)}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-slate-900/80 text-white grid place-items-center hover:bg-rose-600"
            style={{ transition: "background-color 0.15s ease" }}
            aria-label="Remove image"
          >
            <X size={13} />
          </button>
        </div>
      )}
      <div className="mt-3 flex items-center justify-between gap-3">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={(e) => pickFile(e.target.files?.[0] || null)}
          className="hidden"
          data-testid="wb-composer-file-input"
        />
        <button
          type="button"
          data-testid="wb-composer-pick-image"
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-mono-plex tracking-widest uppercase text-slate-600 hover:text-[#00b4ff] hover:bg-slate-50 border border-slate-200"
          style={{ transition: "color 0.15s ease, background-color 0.15s ease" }}
        >
          <ImagePlus size={13} /> Image
        </button>
        <button
          data-testid="wb-composer-submit"
          type="submit"
          disabled={busy || (!text.trim() && !file)}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-display font-semibold"
          style={{ transition: "background-color 0.2s ease" }}
        >
          <SendIcon size={13} className="translate-x-[1px] -translate-y-[1px]" /> {busy ? "Posting…" : "Post"}
        </button>
      </div>
    </form>
  );
}

export default function Whiteboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/whiteboard"); setPosts(data); }
    catch (err) { logDev(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPosted = (post) => setPosts((prev) => [post, ...prev]);
  const onDeletePost = async (id) => {
    if (!window.confirm("Delete post ini?")) return;
    try {
      await api.delete(`/whiteboard/${id}`);
      setPosts((prev) => prev.filter((p) => p.id !== id));
    } catch { toast.error("Delete failed"); }
  };
  const onCommentAdded = (postId, c) => {
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, comments: [...p.comments, c] } : p)));
  };
  const onCommentDeleted = async (postId, commentId) => {
    try {
      await api.delete(`/whiteboard/${postId}/comments/${commentId}`);
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, comments: p.comments.filter((c) => c.id !== commentId) } : p)));
    } catch { toast.error("Delete failed"); }
  };

  // Optimistic toggle helpers — mirror the community-chat pattern.
  const togglePostReaction = async (postId, emoji) => {
    setPosts((prev) => prev.map((p) => {
      if (p.id !== postId) return p;
      const reactions = { ...(p.reactions || {}) };
      const users = [...(reactions[emoji] || [])];
      const idx = users.indexOf(user.id);
      if (idx >= 0) users.splice(idx, 1); else users.push(user.id);
      if (users.length) reactions[emoji] = users; else delete reactions[emoji];
      return { ...p, reactions };
    }));
    try { await api.post(`/whiteboard/${postId}/react`, { emoji }); }
    catch { toast.error("Reaction failed"); }
  };

  const toggleCommentReaction = async (postId, commentId, emoji) => {
    setPosts((prev) => prev.map((p) => {
      if (p.id !== postId) return p;
      return {
        ...p,
        comments: p.comments.map((c) => {
          if (c.id !== commentId) return c;
          const reactions = { ...(c.reactions || {}) };
          const users = [...(reactions[emoji] || [])];
          const idx = users.indexOf(user.id);
          if (idx >= 0) users.splice(idx, 1); else users.push(user.id);
          if (users.length) reactions[emoji] = users; else delete reactions[emoji];
          return { ...c, reactions };
        }),
      };
    }));
    try { await api.post(`/whiteboard/${postId}/comments/${commentId}/react`, { emoji }); }
    catch { toast.error("Reaction failed"); }
  };

  const togglePin = async (post) => {
    const willPin = !post.pinned;
    try {
      await api.post(`/admin/whiteboard/${post.id}/${willPin ? "pin" : "unpin"}`);
      toast.success(willPin ? "Post pinned to top." : "Post unpinned.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="whiteboard-section">
      <div className="mb-4 rounded-2xl border border-slate-200 bg-gradient-to-br from-[#00b4ff]/5 to-white p-5">
        <div className="text-[10px] font-mono-plex tracking-[0.28em] uppercase text-[#00b4ff]">Community Whiteboard</div>
        <div className="mt-1 font-display text-lg font-semibold tracking-tight">Post chart, tanya soalan, share idea</div>
        <div className="mt-1 text-sm text-slate-600">Semua approved members boleh post + komen. Kongsi setup, tanya feedback dari admin & master traders.</div>
      </div>
      <Composer onPosted={onPosted} />
      {loading ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-500 text-sm">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-500 text-sm">Belum ada post. Jadi yang pertama!</div>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              currentUserId={user?.id}
              isAdmin={isAdmin}
              onDelete={onDeletePost}
              onCommentAdded={onCommentAdded}
              onCommentDeleted={onCommentDeleted}
              onReactPost={togglePostReaction}
              onReactComment={toggleCommentReaction}
              onTogglePin={togglePin}
            />
          ))}
        </div>
      )}
    </div>
  );
}
